// ** Redux Imports
import { createAsyncThunk, createSelector, createSlice, type Middleware } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { Brand, BrandCampaignData, GamificationItem } from '@/components/modules/brands-selector';
import { CampaignItemType } from '@/components/modules/spark/configurator/types';
import { getErrorMessage } from '@/utils/errors';
// Timing for the brand-switch fade lives with the area's other animation tokens.
// `theme/tokens` is a dependency-free constants module, so importing it here adds
// no cycle; it keeps the wrapper's fade and this middleware's delay in lockstep.
import { BRAND_SWITCH } from '@/nova/theme/tokens';

import { RootState } from '../';
import { saveUserPreferences, selectLastBrandIdPref, PREFERENCE_KEYS } from './userPreferences';

// Whether the user has explicitly switched brands THIS session (module-scoped, so
// it resets on a real reload — and on logout via `resetCasinoInstance`). Set only
// on a value-changing user switch (see the middleware below). Once true, the
// cross-device reconciliation listener (`store/index.ts`) must NOT override the
// active brand — an explicit choice always beats a late-arriving server value.
let brandSelectedByUser = false;
export const wasBrandSelectedByUser = (): boolean => brandSelectedByUser;

const DEBUG = false;

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const apiUrl = window.config.userApiUrl + '/casino-instance';

type BrandsListProps =
  | {
      token?: string | null;
    }
  | undefined;

type BrandsListResult = {
  data: {
    data: Brand[];
    total: number;
  };
  success: boolean;
};

export const userBrandsList = createAsyncThunk<BrandsListResult, BrandsListProps, GenericError>(
  'casino-instance/list',
  async (_args, { rejectWithValue, getState }) => {
    try {
      const { token } = _args ?? {};

      const state: RootState = getState() as RootState;

      const authToken = token ?? state.authentication.access_token;

      const headers: Record<string, string> = {};

      if (!isNil(authToken)) {
        headers['Authorization'] = authToken;
      } else {
        return rejectWithValue({
          error: 'No authentication token found',
        });
      }

      const response = await axios.post(`${apiUrl}/list`, {}, { headers });

      if (!isNil(response.data)) {
        return { data: response.data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't get brands list",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

/* ---------- gamification-item activation (Product / Product-detail) ----------

The Product pages turn a brand's gamification features on and off through two
owner-hub endpoints (full contract in `docs/api.owners-hub.product-activation.md`):

  POST {userApiUrl}/casino-instance/activate-item   { casino_instance_id, item_id, tier_id }
  POST {userApiUrl}/casino-instance/deactivate-item { casino_instance_id, item_id }

Both echo the updated `gamification_data.gamification_items[]` record. Semantics
(confirmed against test1):
  • activate-item — activates / renews / re-tiers the feature: sets `active=true`,
    `renew=true` and extends `expires_at`. Activation is DIRECT: no invoice is minted
    and the wallet is NOT debited at call time — the feature is billed on the brand's
    recurring `next_invoice_date` cycle. IF a mid-cycle activation ever returns a
    payable `invoice_id`, the wallet flow settles it via `wallet/pay-invoice`.
  • deactivate-item — cancel-at-period-end: sets `renew=false` while leaving
    `active=true`, so the feature keeps running until `expires_at`, then stops renewing.

ID ASYMMETRY (easy to get wrong): activate-item takes the CATALOG ids — numeric
`item_id` + `tier_id` from `/configurator` — while deactivate-item takes the
INSTANCE-scoped `gamification_items[].id` (a UUID) of the purchased record.

Backend limitation to be aware of: activate-item currently 500s
("Cannot read properties of undefined (reading 'campaign_id')") for any (item, tier)
pair that is NOT in the brand's active price-engine campaign, i.e. new activations,
upgrades and downgrades are rejected today — only a same-tier renewal of an owned
feature succeeds. Callers surface the failure; the fix is backend-side.

Callers refetch `userBrandsList()` after a success so the item's active/renew/tier
state updates everywhere (the selectors read the brand list). */

/** The `gamification_items[]` record echoed by deactivate-item. */
export interface GamificationItemMutationResult {
  type: string;
  item_id: number;
  tier_id: number | string;
  cost: number;
  discounted_cost?: number;
  id: string;
  active: boolean;
  renew: boolean;
  expires_at: string;
  /** Set only if the backend mints a payable invoice; the wallet flow settles it via pay-invoice. */
  invoice_id?: string;
}

/**
 * `activate-item` now mints and RETURNS the activation / upgrade INVOICE itself (status `pending`
 * until settled), not the item record. The card path confirms `provider_reference` (a Stripe
 * PaymentIntent client secret, returned inline); the wallet path settles the invoice `id` via
 * `wallet/pay-invoice`. NOTE: there is no `invoice_id` — the invoice's OWN `id` is the payable id.
 */
export interface ActivationInvoice {
  /** The invoice id — settle via `wallet/pay-invoice`. */
  id: string;
  /** Configurator session backing the invoice's Stripe intent (card-path fallback secret source). */
  session_id: string | null;
  status: string;
  /** The invoice's committed amount, as a string ("59.99") — the FULL charge (no client proration). */
  price: string;
  currency: string;
  /** Stripe PaymentIntent client secret for the card path (returned inline on the response). */
  provider_reference?: string;
  /** Alternate spelling accepted as a fallback. */
  client_secret?: string;
}

/** Activate / renew / re-tier a gamification feature on a brand. Takes the CATALOG
 *  `item_id` + `tier_id`. Returns the minted activation invoice (see {@link ActivationInvoice});
 *  caller settles it (wallet / card) then refetches brands. */
export const activateGamificationItem = createAsyncThunk<
  ActivationInvoice,
  { casino_instance_id: string; item_id: number; tier_id: number },
  GenericError
>(
  'casino-instance/activateItem',
  async ({ casino_instance_id, item_id, tier_id }, { rejectWithValue, getState }) => {
    try {
      const authToken = (getState() as RootState).authentication.access_token;
      if (isNil(authToken)) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.post(
        `${apiUrl}/activate-item`,
        { casino_instance_id, item_id, tier_id },
        { headers: { Authorization: authToken } },
      );
      // The response wraps the minted invoice under an `invoice` key:
      // `{ invoice: { id, provider_reference, session_id, price, ... } }`. Unwrap it (tolerating a
      // future flat shape) so callers read `id` / `provider_reference` / `session_id` directly.
      const body = response.data as { invoice?: ActivationInvoice };
      return (body.invoice ?? (response.data as ActivationInvoice));
    } catch (error) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Cancel a gamification feature's renewal (stays active until `expires_at`). Takes the
 *  INSTANCE-scoped `gamification_items[].id` (UUID), NOT the numeric catalog id. */
export const deactivateGamificationItem = createAsyncThunk<
  GamificationItemMutationResult,
  { casino_instance_id: string; item_id: string },
  GenericError
>(
  'casino-instance/deactivateItem',
  async ({ casino_instance_id, item_id }, { rejectWithValue, getState }) => {
    try {
      const authToken = (getState() as RootState).authentication.access_token;
      if (isNil(authToken)) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.post(
        `${apiUrl}/deactivate-item`,
        { casino_instance_id, item_id },
        { headers: { Authorization: authToken } },
      );
      return response.data as GamificationItemMutationResult;
    } catch (error) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/**
 * Phase of a brand switch, driving `BrandTransition`:
 *  - `idle` — no switch in flight.
 *  - `out`  — fading the current view out (the content swaps to the new brand at
 *             the bottom of the fade, while hidden).
 *  - `in`   — fading the new brand's view in.
 */
export type BrandSwitchPhase = 'idle' | 'out' | 'in';

export interface ConfigReducer {
  brands: Brand[];
  /** The selected brand — updates immediately so the brand switcher's logo /
   *  checkmark react the instant you pick a brand. */
  activeBrand: Brand | undefined;
  /** The brand the page *content* renders. Lags `activeBrand` during a switch so
   *  the previous brand's data stays on screen through the fade-out, then swaps
   *  while hidden. Read by the pages, never by the switcher. */
  displayBrand: Brand | undefined;
  /** Where we are in a brand switch — see {@link BrandSwitchPhase}. */
  brandSwitchPhase: BrandSwitchPhase;
  loaded: boolean;
  loading: boolean;
  error: any;
}

export const casinoInstanceSlice = createSlice({
  name: 'casinoInstance',
  initialState: <ConfigReducer>{
    brands: [],
    activeBrand: undefined,
    displayBrand: undefined,
    brandSwitchPhase: 'idle',
    loaded: false,
    loading: false,
    error: null,
  },
  reducers: {
    setActiveBrand: (state, action) => {
      state.activeBrand = action.payload;
    },
    setDisplayBrand: (state, action) => {
      state.displayBrand = action.payload;
    },
    setBrandSwitchPhase: (state, action) => {
      state.brandSwitchPhase = action.payload;
    },
    /**
     * Wipe ALL brand state — both the Redux slice and the two persisted choices
     * (`selectedBrandId`, `pendingBrandSessionId`). Dispatched by the central
     * session-teardown listener (`store/index.ts`) on logout / auth-check
     * rejection, so a stale brand id from a previous (or expired) session can
     * never be read first on the next load. Resetting `loaded` back to `false` is
     * deliberate: a later component must re-fetch before anything trusts the list
     * as "empty" again.
     */
    resetCasinoInstance: (state) => {
      state.brands = [];
      state.activeBrand = undefined;
      state.displayBrand = undefined;
      state.brandSwitchPhase = 'idle';
      state.loaded = false;
      state.loading = false;
      state.error = null;
      clearSelectedBrandId();
      clearPendingBrandSessionId();
      brandSelectedByUser = false;
    },
    /**
     * Drop the PREVIOUS identity's brand choice WITHOUT wiping the brand list.
     *
     * Used when a support agent switches the impersonated owner mid-session via a
     * magic link WITHOUT logging out: the magic-link flow re-fetches the new owner's
     * `brands`, but the persisted selection (`selectedBrandId` localStorage), the
     * in-session "user switched" flag (`brandSelectedByUser`, which suppresses the
     * cross-device `lastBrandId` reconciliation in `store/index.ts`), and a now-stale
     * `activeBrand` object from the old identity all survived. Clear those and
     * re-resolve the active/display brand from the CURRENT list, so the new owner's
     * saved `lastBrandId` can take over once their preferences reload. Unlike
     * `resetCasinoInstance` this keeps `brands`/`loaded` intact (no refetch needed —
     * the list already belongs to the new owner).
     */
    resetBrandSelection: (state) => {
      clearSelectedBrandId();
      clearPendingBrandSessionId();
      brandSelectedByUser = false;
      const next = resolveInitialBrand(state.brands);
      state.activeBrand = next;
      state.displayBrand = next;
      state.brandSwitchPhase = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userBrandsList.fulfilled, (state, action) => {
        state.loaded = true;
        state.loading = false;
        if (action.payload.success) {
          state.brands = action.payload.data.data;
        }
        DEBUG && console.timeEnd('userBrandsList.query');
        DEBUG && console.log('userBrandsList.fulfilled', action.payload);
      })
      .addCase(userBrandsList.pending, (state) => {
        state.loaded = false;
        state.loading = true;
        DEBUG && console.time('userBrandsList.query');
      })
      .addCase(userBrandsList.rejected, (state, action) => {
        state.loaded = false;
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('userBrandsList.rejected', action.payload);
      });
  },
});

export const { setActiveBrand, setDisplayBrand, setBrandSwitchPhase, resetCasinoInstance, resetBrandSelection } =
  casinoInstanceSlice.actions;

// ── Selected-brand persistence ──────────────────────────────────────────────
// The active brand lives only in Redux, which resets on reload — so the switcher
// always fell back to `brands[0]`. We persist the choice in two places:
//   • localStorage (keyed globally, like the auth tokens) — the SYNCHRONOUS fast
//     path read by `resolveInitialBrand` on load, so the switcher is never blank;
//   • the user-preferences API (`lastBrandId`) — the cross-device source of truth,
//     reconciled against the active brand by the `brandPrefSyncListener` in
//     `store/index.ts` once both the brand list and the preferences have loaded.
// Both are validated against the live list so a stale id silently falls back to
// the default. The prefs write happens at the switch chokepoint below.
const SELECTED_BRAND_KEY = 'selectedBrandId';
// A brand created through the deploy flow has no id until the backend provisions
// it; it's linked to its deploy session via `deployments[].session_id`. We
// remember that session id at deploy time and adopt the matching brand the
// instant it appears in the list — so a freshly-created brand becomes active
// instead of `brands[0]`.
const PENDING_BRAND_SESSION_KEY = 'pendingBrandSessionId';

export const readSelectedBrandId = (): string | null => {
  try {
    return localStorage.getItem(SELECTED_BRAND_KEY);
  } catch {
    return null;
  }
};

export const writeSelectedBrandId = (id: string): void => {
  try {
    localStorage.setItem(SELECTED_BRAND_KEY, id);
  } catch {
    // Storage may be unavailable (private mode / quota) — selection still works
    // for this session, it just won't survive a reload.
  }
};

export const clearSelectedBrandId = (): void => {
  try {
    localStorage.removeItem(SELECTED_BRAND_KEY);
  } catch {
    // ignore — see writeSelectedBrandId
  }
};

export const readPendingBrandSessionId = (): string | null => {
  try {
    return localStorage.getItem(PENDING_BRAND_SESSION_KEY);
  } catch {
    return null;
  }
};

export const writePendingBrandSessionId = (sessionId: string): void => {
  try {
    localStorage.setItem(PENDING_BRAND_SESSION_KEY, sessionId);
  } catch {
    // ignore — see writeSelectedBrandId
  }
};

export const clearPendingBrandSessionId = (): void => {
  try {
    localStorage.removeItem(PENDING_BRAND_SESSION_KEY);
  } catch {
    // ignore — see writeSelectedBrandId
  }
};

/** The brand to activate on load when nothing is selected yet: the persisted
 *  choice if it's still in the list, otherwise the first brand. Returns
 *  `undefined` only for an empty list. The freshly-deployed-brand case is
 *  handled by the switcher (it can override an already-active brand), keyed off
 *  {@link readPendingBrandSessionId}. */
export const resolveInitialBrand = (brands: Brand[]): Brand | undefined => {
  if (brands.length === 0) {
    return undefined;
  }

  const persistedId = readSelectedBrandId();
  if (persistedId) {
    const match = brands.find((brand) => brand.id === persistedId);
    if (match) {
      return match;
    }
  }

  return brands[0];
};

/**
 * Brand-switch choreography.
 *
 * `setActiveBrand` is applied immediately (so the brand switcher's logo/checkmark
 * react the instant you click), and on a genuine switch this middleware then runs
 * the transition — fading the view OUT while it still shows the *previous* brand,
 * then fading the new brand IN. The trick is that the page content reads
 * `displayBrand`, NOT `activeBrand`, and this only promotes `displayBrand` to the
 * new brand at the bottom of the fade (while hidden):
 *
 *   out ──FADE_OUT_MS──▶ (displayBrand = new) in ──FADE_IN_MS──▶ idle
 *
 * `BrandTransition` reads `brandSwitchPhase` to fade the page.
 * The first selection (no previous brand) and same-brand re-selects promote
 * `displayBrand` straight away with no transition. Registered ahead of saga/async
 * middleware in `store/index.ts`.
 *
 * The `seq` guard + timer list make a rapid re-switch restart the timeline cleanly
 * (stale callbacks bail) and land on the most recently chosen brand.
 */
let switchSeq = 0;
let switchTimers: number[] = [];

const clearSwitchTimers = (): void => {
  switchTimers.forEach((t) => window.clearTimeout(t));
  switchTimers = [];
};

// Typed as the bare `Middleware` (not parameterised by `RootState`): `RootState`
// is derived from the store that concatenates this very middleware, so naming it
// in the annotation would be circular. `getState()` is cast to `RootState` in the
// body instead.
export const brandSwitchMiddleware: Middleware = (store) => (next) => (action: any) => {
    if (!setActiveBrand.match(action)) {
      return next(action);
    }
    // Capture the brand on screen *before* applying the selection, then let the
    // selection through so the switcher updates at once.
    const previous = (store.getState() as RootState).casinoInstance.activeBrand;
    const incoming = action.payload as Brand | undefined;
    const result = next(action);

    // Persist whatever is now selected so a reload restores it. Every selection
    // — manual click or the switcher's auto-resolution — flows through here, so
    // this is the single chokepoint.
    if (incoming) {
      // localStorage always remembers the latest selection (same-device fast path,
      // including the auto-resolved default), so a reload is never blank.
      writeSelectedBrandId(incoming.id);
      // Mirror to the preferences API (cross-device) ONLY on a genuine, value-
      // changing user switch — i.e. a brand was already active (`previous` defined)
      // and the choice differs from what's persisted. The initial auto-resolve
      // (`previous` undefined: localStorage / brands[0]) is chosen WITHOUT knowing
      // the server value, so persisting it could clobber another device's choice;
      // and the reconciliation listener's own restore matches the persisted value,
      // so it no-ops here. We do NOT gate on `prefs.loaded`: a user who switches
      // before prefs finish loading must still have their choice POSTed — and we
      // mark `brandSelectedByUser` so the listener can't later override it with a
      // late-arriving server value. `selectLastBrandIdPref` gives a typed
      // `string | null`, so the `!== incoming.id` check still fires on the first
      // save (null !== id).
      const savedBrandId = selectLastBrandIdPref(store.getState() as RootState);
      if (!isNil(previous) && savedBrandId !== incoming.id) {
        brandSelectedByUser = true;
        // `store.dispatch` here is typed as the bare `Dispatch` (the middleware is
        // declared as the un-parameterised `Middleware`, see note above), which
        // doesn't accept a thunk — cast to dispatch the async save. Fire-and-forget:
        // cross-device sync is best-effort (localStorage is the durable same-device
        // store); a failure leaves `saveError` in the slice for optional surfacing.
        (store.dispatch as any)(saveUserPreferences({ [PREFERENCE_KEYS.lastBrandId]: incoming.id }));
      }
    }

    if (isNil(previous) || isNil(incoming) || previous.id === incoming.id) {
      // First selection / same brand: nothing to transition — show it now.
      store.dispatch(setDisplayBrand(incoming));
      return result;
    }

    // Genuine switch: run the fade → load → fade timeline, promoting the content
    // brand (`displayBrand`) only once the view is hidden behind the loader.
    clearSwitchTimers();
    const seq = (switchSeq += 1);
    const stale = (): boolean => seq !== switchSeq;
    const { FADE_OUT_MS, FADE_IN_MS } = BRAND_SWITCH;

    store.dispatch(setBrandSwitchPhase('out'));
    switchTimers.push(
      window.setTimeout(() => {
        if (stale()) return;
        store.dispatch(setDisplayBrand(incoming)); // content swaps while hidden
        store.dispatch(setBrandSwitchPhase('in'));
      }, FADE_OUT_MS),
      window.setTimeout(() => {
        if (stale()) return;
        store.dispatch(setBrandSwitchPhase('idle'));
      }, FADE_OUT_MS + FADE_IN_MS),
    );
    return result;
  };

/** The displayed brand's purchased gamification items (base item excluded).
 *  Reads `displayBrand` (not `activeBrand`) so the Product pages' items lag
 *  through a brand switch's fade-out, matching the rest of the page content. */
export const selectActiveBrandGamificationItems = createSelector(
  [(state: RootState) => state.casinoInstance.displayBrand],
  (activeBrand): GamificationItem[] =>
    (activeBrand?.gamification_data.gamification_items ?? [])
      .filter((item) => item.type === CampaignItemType.GAMIFICATION_ITEM)
      // `casino-instance/list` sends `item_id` / `tier_id` as STRINGS ("44" / "40") even
      // though the types declare them numeric — and the catalog ids they join against ARE
      // numbers. Coerce here, at the single source every Product page reads, so each
      // `=== catalogId` join is exact; otherwise an owned feature never matches its catalog
      // entry and renders as inactive / "missing". Gamification tier ids are always numeric
      // (the non-numeric "BASIC" tier belongs to the BASE_ITEM filtered out above).
      .map((item) => ({ ...item, item_id: Number(item.item_id), tier_id: Number(item.tier_id) })),
);

/** The displayed brand's active price-engine campaign (`gamification_data.campaign_data`).
 *  This is the source of truth for Product-page prices: each `campaign.items[].tiers[]`
 *  carries the REAL `base_price` / `discounted_price`, and `currency` the billing currency
 *  — the `/configurator` catalog's tier cost tables are stale placeholders and must NOT be
 *  used for pricing. Reads `displayBrand` (not `activeBrand`) so it lags a brand switch's
 *  fade in lockstep with the page content. Undefined when the brand has no campaign. */
export const selectActiveBrandCampaign = createSelector(
  [(state: RootState) => state.casinoInstance.displayBrand],
  (activeBrand): BrandCampaignData | undefined => activeBrand?.gamification_data.campaign_data,
);

/** ISO-4217 currency the currently-selected casino (the displayed brand) is billed in — the
 *  brand's active campaign currency. `undefined` when the brand has no campaign yet (e.g.
 *  mid-deploy), so callers fall back to the wallet's own currency. Reads `displayBrand` (like the
 *  price selectors) so it lags a brand switch's fade in lockstep with the page content. Used to
 *  pick the MAIN wallet a charge/top-up is funded from (see `resolveMainWalletForCurrency`). */
export const selectSelectedCasinoCurrency = createSelector(
  [(state: RootState) => state.casinoInstance.displayBrand],
  (brand): string | undefined =>
    brand?.gamification_data?.campaign_data?.currency ??
    brand?.gamification_data?.campaign_data?.campaign?.currency_code ??
    undefined,
);

export default casinoInstanceSlice.reducer;
